import { ArrowLeftRight } from "lucide-react";
import { useId } from "react";
import Flag from "react-world-flags";

import { CurrencyFlag } from "@/modules/dashboard/wallets/components/CurrencyFlag";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import useTranslations from "@/shared/lang/useTranslations";

import { PAYOUT_WIZARD_COUNTRIES } from "../../constants/dispersions.constants";
import { formatSendMoney } from "./formatSendMoney";

export interface SendRouteStripProps {
  /** The wallets the money can leave from; the selected one first decides the balance shown. */
  origins: RuntimeWallet[];
  originId: string;
  onOriginChange: (walletId: string) => void;
  /** ISO3 of the destination corridor. */
  destination: string;
  onDestinationChange: (iso3: string) => void;
  /** Whether a corridor can pay out right now; an unavailable one is listed, disabled. */
  isDestinationAvailable: (iso3: string) => boolean;
}

const LABEL = "w-14 shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

/**
 * Where the money leaves from and where it goes. Both stay inside the panel: a destination paid in another
 * currency is converted inside the payout, so picking one only changes the price, never the screen.
 */
export function SendRouteStrip({
  origins,
  originId,
  onOriginChange,
  destination,
  onDestinationChange,
  isDestinationAvailable,
}: SendRouteStripProps) {
  const { t, activeLanguage } = useTranslations("dispersions");
  const fromLabelId = useId();
  const toLabelId = useId();
  const origin = origins.find((wallet) => wallet.id === originId) ?? origins[0];
  const destinationCountry = PAYOUT_WIZARD_COUNTRIES.find((country) => country.code === destination);
  const converts = !!origin && !!destinationCountry && origin.currency !== destinationCountry.currency;

  const originLabel = (wallet: RuntimeWallet) => (
    <span className="flex items-center gap-2">
      <CurrencyFlag currency={wallet.currency} />
      {t("send_from_wallet", { currency: wallet.currency })}
    </span>
  );

  const destinationOptions = PAYOUT_WIZARD_COUNTRIES.map((country) => ({
    value: country.code,
    label: (
      <span className="flex items-center gap-2">
        <Flag code={country.flag} className="h-3 w-[18px] shrink-0 rounded-[2px] object-cover" aria-hidden />
        {country.name}
        <span className="font-mono text-[11px] text-muted-foreground">{country.currency}</span>
      </span>
    ),
    disabled: country.code !== destination && !isDestinationAvailable(country.code),
  }));

  return (
    <section className="space-y-2">
      <div className="divide-y divide-border rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
          <span id={fromLabelId} className={LABEL}>
            {t("send_from_label")}
          </span>
          {origins.length > 1 ? (
            <ColocaSelect
              value={origin?.id ?? ""}
              onValueChange={(value) => value && onOriginChange(value)}
              options={origins.map((wallet) => ({ value: wallet.id, label: originLabel(wallet) }))}
              size="sm"
              // Wide enough for "COP wallet": on a phone the balance wraps under it instead of eating it.
              className="min-w-40 flex-1"
              aria-labelledby={fromLabelId}
            />
          ) : (
            origin && (
              <span className="min-w-0 flex-1 py-0.5 text-sm font-semibold text-foreground">
                {originLabel(origin)}
              </span>
            )
          )}
          {origin && (
            <span className="ml-auto font-mono text-xs text-muted-foreground">
              {t("send_available", {
                amount: formatSendMoney(origin.balance_available, origin.currency, activeLanguage),
              })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 px-3 py-2">
          <span id={toLabelId} className={LABEL}>
            {t("send_to_label")}
          </span>
          <ColocaSelect
            value={destination}
            onValueChange={(value) => value && onDestinationChange(value)}
            options={destinationOptions}
            size="sm"
            className="min-w-0 flex-1"
            aria-labelledby={toLabelId}
          />
        </div>
      </div>
      {converts && origin && destinationCountry && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ArrowLeftRight className="mt-px size-3.5 shrink-0" aria-hidden="true" />
          {t("send_converts_hint", { from: origin.currency, to: destinationCountry.currency })}
        </p>
      )}
    </section>
  );
}
